import React from "react";
import "./stylesTableQF.css";
import { iTxt } from "@duosoftbg/nacid-components";
import { getI18n, useTranslation } from "react-i18next";
import { ReactComponent as TableDashedIcon } from "./icons/table-dashed.svg";
import { ReactComponent as LayoutGrid } from "./icons/layout-grid.svg";
import { ReactComponent as ButtonUp } from "./icons/ButtonUp.svg";
import { ReactComponent as ButtonLeft } from "./icons/ButtonLeft.svg";
import { ReactComponent as ButtonRight } from "./icons/ButtonRight.svg";
import { ReactComponent as ButtonDown } from "./icons/ButtonDown.svg";
import Tooltip from "@mui/material/Tooltip";
import { getColor, tableTooltipProps } from "./stylesTableQfTS";
import {
  eqfLevelNumberH,
  mainHeaders,
  sideHeaders,
  subHeaders1,
  subHeaders2,
  tooltipMainHeader,
  tooltipSideHeader,
} from "./dataTableQf";
import { CountryDetailsLong, CountryQF } from "../../../types/QFDataTypes";

interface CountryQFTableProps {
  countryData: CountryDetailsLong;
  handleTables: (direction: "H" | "V") => void;
  setLogicalTableMapH: (value: boolean[][]) => void;
  setColumnSwitchH: (value: boolean[]) => void;
  setRowSwitchH: (value: boolean[]) => void;
  logicalTableMapH: boolean[][];
  columnSwitchH: boolean[];
  rowSwitchH: boolean[];
}

export const QFTableHorizontal = ({
  countryData,
  handleTables,
  setLogicalTableMapH,
  setColumnSwitchH,
  setRowSwitchH,
  logicalTableMapH,
  columnSwitchH,
  rowSwitchH,
}: CountryQFTableProps) => {
  const { t } = useTranslation();

  const handleColumnSwitchH = (index: number) => {
    // @ts-ignore
    setLogicalTableMapH((prev: boolean[][]) => {
      return prev.map((row) => row.map((cell, c) => (c === index ? !columnSwitchH[index] : cell)));
    });
    // @ts-ignore
    setColumnSwitchH((prev: boolean[]) => {
      return prev.map((val, i) => (i === index ? !val : val));
    });
  };

  const handleRowSwitchH = (index: number) => {
    // @ts-ignore
    setLogicalTableMapH((prev: boolean[][]) => {
      return prev.map((row, i) => {
        return i === index ? row.map(() => !rowSwitchH[index]) : row;
      });
    });

    // @ts-ignore
    setRowSwitchH((prev: boolean[]) => {
      return prev.map((val, i) => (i === index ? !val : val));
    });
  };

  const isWholeTableHiddenH = () => {
    return rowSwitchH.every((l) => l === true) && columnSwitchH.every((l) => l === true);
  };

  const handleWholeTableH = () => {
    // @ts-ignore
    setLogicalTableMapH((prev: boolean[][]) => {
      return prev.map((row) => row.map(() => !isWholeTableHiddenH()));
    });
  };

  const isHiddenCell = (rowIndex: number, colIndex: number) => {
    return logicalTableMapH[rowIndex][colIndex] === true;
  };
  const isMainHeaderCellHide = (index: number) => {
    return logicalTableMapH.map((col) => col[index]).every((l) => l === true);
  };
  const isSideHeaderCellHide = (index: number) => {
    return logicalTableMapH.map((col) => col[index + 2]).every((l) => l === true);
  };
  const cornerButtonsFlag =
    logicalTableMapH.map((col) => col[0]).every((l) => l === true) &&
    logicalTableMapH.map((col) => col[1]).every((l) => l === true);

  const spanRowHeaders_0 = isMainHeaderCellHide(0) && !isMainHeaderCellHide(1) ? 3 : 2;
  const spanRowHeaders_1 = !isMainHeaderCellHide(0) && isMainHeaderCellHide(1) ? 3 : 2;
  const spanRowSideHeaders = cornerButtonsFlag ? 2 : 3;

  const countriesQFMap = new Map<number, CountryQF[]>();
  eqfLevelNumberH.forEach((eqfLevel) => {
    countriesQFMap.set(
      eqfLevel,
      countryData.countriesQF.filter((qf) => qf.eqfLevel.id === eqfLevel).sort((a, b) => b.level - a.level),
    );
  });

  const tableMap = new Map<number, number>();
  for (let i = 0; i < 3; i++) {
    tableMap.set(
      eqfLevelNumberH[i],
      countriesQFMap
        .get(eqfLevelNumberH[i])
        .reduce((sum: number, el) => sum + (!logicalTableMapH[i][1] ? el.levels.length : 1), 0),
    );
  }

  return (
    <>
      <div
        key="table"
        className="table fade-slide"
        style={{
          gridTemplateColumns: cornerButtonsFlag ? "40px 40px repeat(11, auto)" : "40px repeat(12, auto)",
        }}
      >
        {/* === 1 or 2 table-control button(s) === */}
        <div key="btnD" className="iconPush-cell" style={{ gridColumn: cornerButtonsFlag ? "span 1" : "span 2" }}>
          <TableDashedIcon onClick={() => handleTables("H")} />
        </div>

        {cornerButtonsFlag && (
          <div key="btnL" className="iconPop-cell" style={{ gridColumn: "span 1" }}>
            <LayoutGrid onClick={() => handleTables("V")} />
          </div>
        )}

        {/* === 2 main headers === */}
        {mainHeaders(t, countryData, isMainHeaderCellHide).map((title, index) => (
          <Tooltip
            key={isMainHeaderCellHide(index) ? `ttm-c-${index}` : `ttm-o-${index}`}
            title={tooltipMainHeader(t, countryData)[index]}
            placement="bottom"
            componentsProps={tableTooltipProps}
            disableHoverListener={!isMainHeaderCellHide(index)}
          >
            <div
              key={`mh-${index}`}
              className="header-cell fade-slide"
              style={{
                gridRow: `span ${index === 0 ? spanRowHeaders_0 : spanRowHeaders_1}`,
                gridColumn: "span 4",
                backgroundColor: getColor(1),
              }}
            >
              <span style={{ paddingRight: "25px" }}>{title}</span>
              {!columnSwitchH[index] ? (
                <ButtonLeft className="iconButtonLeftRight-cell" onClick={() => handleColumnSwitchH(index)} />
              ) : (
                <ButtonRight className="iconButtonLeftRight-cell" onClick={() => handleColumnSwitchH(index)} />
              )}
            </div>
          </Tooltip>
        ))}

        {/* === 3 side headers === */}
        {sideHeaders(t, isSideHeaderCellHide).map((title, index) => (
          <Tooltip
            key={isSideHeaderCellHide(index) ? `tts-c-${index}` : `tts-o-${index}`}
            title={tooltipSideHeader(t)[index]}
            placement="bottom"
            componentsProps={tableTooltipProps}
            disableHoverListener={!isSideHeaderCellHide(index)}
          >
            <div
              key={`sh-${index}`}
              className="header-cell fade-slide"
              style={{
                gridRow: `span ${spanRowSideHeaders}`,
                backgroundColor: getColor(1),
              }}
            >
              <span style={{ paddingRight: "25px", marginBottom: "1px" }}>{title}</span>
              {!columnSwitchH[index + 2] ? (
                <ButtonLeft className="iconButtonLeftRight-cell" onClick={() => handleColumnSwitchH(index + 2)} />
              ) : (
                <ButtonRight className="iconButtonLeftRight-cell" onClick={() => handleColumnSwitchH(index + 2)} />
              )}
            </div>
          </Tooltip>
        ))}

        {/* === 2nd and 3rd button to control === */}
        {!cornerButtonsFlag && (
          <div key="btnL" className="iconPop-cell" style={{ gridColumn: "span 2" }}>
            <LayoutGrid onClick={() => handleTables("V")} />
          </div>
        )}

        <div
          key="btnU"
          className="iconButtonUp-cell"
          style={{
            backgroundColor: getColor(2),
            gridColumn: "span 2",
          }}
        >
          {!isWholeTableHiddenH() ? (
            <ButtonUp onClick={() => handleWholeTableH()} />
          ) : (
            <ButtonDown onClick={() => handleWholeTableH()} />
          )}
        </div>

        {/* === 1st array of subheaders === */}
        {!isMainHeaderCellHide(0) &&
          subHeaders1(t).map((title, index) => (
            <div
              key={`subH-0-${index}`}
              className="subheader-cell fade-slide"
              style={{
                gridRow: "span 1",
                backgroundColor: getColor(2),
              }}
            >
              {title}
            </div>
          ))}

        {/* === 2nd array of subheaders === */}
        {!isMainHeaderCellHide(1) &&
          subHeaders2(t).map((title, index) => (
            <div
              key={`subH-1-${index}`}
              className="subheader-cell fade-slide"
              style={{
                gridRow: "span 1",
                backgroundColor: getColor(2),
              }}
            >
              {title}
            </div>
          ))}

        {/* === EQF level rows === */}
        {eqfLevelNumberH.map((eqfLN, index) => {
          const levels = countriesQFMap.get(eqfLN) || [];

          return levels.map((level, indexLevel) => {
            const sublevels = level.levels || [];
            const startRowSubLevels = sublevels[0];
            const restRowsSubLevels = sublevels.slice(1);
            const subLevelMessageBg = `${sublevels.length} ${
              sublevels.length === 1 ? "степен" : "степени"
            } на ${level.name}`;
            const subLevelMessageEn = `${sublevels.length} ${
              sublevels.length === 1 ? "degree" : "degrees"
            } of ${level.nameEn}`;

            return (
              <React.Fragment key={`eqf-block-${eqfLN}-${indexLevel}`}>
                {/* === Left control button per EQF level === */}
                {indexLevel === 0 && (
                  <div
                    key={`btnU-${index}`}
                    className="iconButtonUp-cell"
                    style={{
                      backgroundColor: getColor(eqfLN),
                      gridRow: `span ${tableMap.get(eqfLN)}`,
                      gridColumn: "span 2",
                    }}
                  >
                    {!rowSwitchH[index] ? (
                      <ButtonUp onClick={() => handleRowSwitchH(index)} />
                    ) : (
                      <ButtonDown onClick={() => handleRowSwitchH(index)} />
                    )}
                  </div>
                )}

                {/* === Main cell block === */}
                <div
                  key={`cellA-${index}-${isHiddenCell(index, 0) ? 1 : 0}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(eqfLN),
                    gridColumn: `span ${isHiddenCell(index, 0) ? 4 : 1}`,
                    gridRow: `span ${isHiddenCell(index, 1) ? 1 : sublevels.length}`,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{iTxt(getI18n().language, level.name, level.nameEn)}</div>
                  {!isHiddenCell(index, 0) && (
                    <div>{iTxt(getI18n().language, level.description, level.descriptionEn)}</div>
                  )}
                </div>

                {!isHiddenCell(index, 0) && (
                  <>
                    <div
                      key={`cellB-${index}`}
                      className="common-cell fade-slide"
                      style={{
                        backgroundColor: getColor(eqfLN),
                        gridRow: `span ${isHiddenCell(index, 1) ? 1 : sublevels.length}`,
                      }}
                    >
                      {level.nameNative}
                    </div>

                    <div
                      key={`cellC-${index}`}
                      className="common-cell fade-slide"
                      style={{
                        backgroundColor: getColor(eqfLN),
                        gridRow: `span ${isHiddenCell(index, 1) ? 1 : sublevels.length}`,
                      }}
                    >
                      {level.credits ? `${level.credits} ${countryData.creditsLabel}` : "-"}
                    </div>

                    <div
                      key={`cellD-${index}`}
                      className="common-cell fade-slide"
                      style={{
                        backgroundColor: getColor(eqfLN),
                        gridRow: `span ${isHiddenCell(index, 1) ? 1 : sublevels.length}`,
                      }}
                    >
                      {level.duration ? `${level.duration}${iTxt(getI18n().language, " г.", " y")}` : "-"}
                    </div>
                  </>
                )}

                {/* === Sublevels block === */}
                <div
                  key={`cellE-${index}-${isHiddenCell(index, 1) ? 1 : 0}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(eqfLN),
                    gridRow: "span 1",
                    gridColumn: `span ${isHiddenCell(index, 1) ? 4 : 1}`,
                  }}
                >
                  {isHiddenCell(index, 1)
                    ? iTxt(getI18n().language, subLevelMessageBg, subLevelMessageEn)
                    : iTxt(getI18n().language, startRowSubLevels.name, startRowSubLevels.nameEn)}
                </div>

                {!isHiddenCell(index, 1) && (
                  <>
                    <div
                      key={`cellF-${index}`}
                      className="common-cell fade-slide"
                      style={{ backgroundColor: getColor(eqfLN) }}
                    >
                      {startRowSubLevels.nameNative}
                    </div>

                    <div
                      key={`cellG-${index}`}
                      className="common-cell fade-slide"
                      style={{ backgroundColor: getColor(eqfLN) }}
                    >
                      {startRowSubLevels.credits ? `${startRowSubLevels.credits} ${countryData.creditsLabel}` : "-"}
                    </div>

                    <div
                      key={`cellH-${index}`}
                      className="common-cell fade-slide"
                      style={{ backgroundColor: getColor(eqfLN) }}
                    >
                      {startRowSubLevels.duration
                        ? `${startRowSubLevels.duration}${iTxt(getI18n().language, " г.", " y")}`
                        : "-"}
                    </div>
                  </>
                )}

                {/* === EQF / Bologna / BG Level === */}
                {indexLevel === 0 && (
                  <>
                    {/* EQF */}
                    <div
                      key={`cellI-${index}-${isHiddenCell(index, 2) ? 1 : 0}`}
                      className="common-cell fade-slide"
                      style={{
                        backgroundColor: getColor(eqfLN),
                        gridRow: `span ${tableMap.get(eqfLN)}`,
                      }}
                    >
                      <div style={{ fontWeight: "bold" }}>
                        {iTxt(getI18n().language, level?.eqfLevel?.name, level?.eqfLevel?.nameEn)}
                      </div>
                      {!isHiddenCell(index, 2) && (
                        <div>
                          {iTxt(getI18n().language, level?.eqfLevel?.description, level?.eqfLevel?.descriptionEn)}
                        </div>
                      )}
                    </div>

                    {/* Bologna */}
                    <div
                      key={`cellJ-${index}-${isHiddenCell(index, 3) ? 1 : 0}`}
                      className="common-cell fade-slide"
                      style={{
                        backgroundColor: getColor(eqfLN),
                        gridRow: `span ${tableMap.get(eqfLN)}`,
                      }}
                    >
                      <div style={{ fontWeight: "bold" }}>
                        {iTxt(getI18n().language, level?.bolognaCycle?.name, level?.bolognaCycle?.nameEn)}
                      </div>
                      {!isHiddenCell(index, 3) && (
                        <div>
                          {iTxt(
                            getI18n().language,
                            level?.bolognaCycle?.description,
                            level?.bolognaCycle?.descriptionEn,
                          )}
                        </div>
                      )}
                    </div>

                    {/* BG Level */}
                    <div
                      key={`cellK-${index}-${isHiddenCell(index, 4) ? 1 : 0}`}
                      className="common-cell fade-slide"
                      style={{
                        backgroundColor: getColor(eqfLN),
                        gridRow: `span ${tableMap.get(eqfLN)}`,
                      }}
                    >
                      <div style={{ fontWeight: "bold" }}>
                        {iTxt(getI18n().language, level?.bgLevel?.name, level?.bgLevel?.nameEn)}
                      </div>
                      {!isHiddenCell(index, 4) && (
                        <div>
                          {iTxt(getI18n().language, level?.bgLevel?.description, level?.bgLevel?.descriptionEn)}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* === Remaining sublevels === */}
                {!isHiddenCell(index, 1) &&
                  restRowsSubLevels.map((subLevel, idx) => (
                    <React.Fragment key={`rest-${index}-${idx}`}>
                      <div
                        key={`cellA-${index}-${idx}`}
                        className="common-cell fade-slide"
                        style={{ backgroundColor: getColor(eqfLN) }}
                      >
                        {iTxt(getI18n().language, subLevel.name, subLevel.nameEn)}
                      </div>
                      <div
                        key={`cellB-${index}-${idx}`}
                        className="common-cell fade-slide"
                        style={{ backgroundColor: getColor(eqfLN) }}
                      >
                        {subLevel.nameNative}
                      </div>
                      <div
                        key={`cellC-${index}-${idx}`}
                        className="common-cell fade-slide"
                        style={{ backgroundColor: getColor(eqfLN) }}
                      >
                        {subLevel.credits ? `${subLevel.credits} ${countryData.creditsLabel}` : "-"}
                      </div>
                      <div
                        key={`cellD-${index}-${idx}`}
                        className="common-cell fade-slide"
                        style={{ backgroundColor: getColor(eqfLN) }}
                      >
                        {subLevel.duration ? `${subLevel.duration}${iTxt(getI18n().language, " г.", " y")}` : "-"}
                      </div>
                    </React.Fragment>
                  ))}
              </React.Fragment>
            );
          });
        })}
      </div>
    </>
  );
};
